import { createFileRoute } from "@tanstack/react-router";
import { Apple, ArrowRight, Linkedin, LogOut, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { SaleskoOAuthProvider } from "@salesko/contracts";
import { GoogleGlyph } from "../components/GoogleGlyph";
import { LOCALE_HTML_LANG, LanguageSwitcher, useI18n } from "../i18n";
import {
  clearStoredSaleskoSession,
  consumePendingBillingCheckoutIntent,
  readPendingBillingCheckoutIntent,
  startCheckout
} from "../lib/api";
import { useAgentSession } from "../workspace/hooks/useAgentSession";

const PRIVACY_URL = "https://salesko.ai/privacy";
const TERMS_URL = "https://salesko.ai/terms";

const PROVIDER_META: Record<SaleskoOAuthProvider, { label: string; icon?: LucideIcon }> = {
  google: { label: "Google" },
  apple: { label: "Apple", icon: Apple },
  linkedin: { label: "LinkedIn", icon: Linkedin }
};

export const Route = createFileRoute("/login")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sign in - Salesko" },
      {
        name: "description",
        content: "Sign in to the Salesko contacts intelligence workspace."
      },
      { name: "robots", content: "noindex, nofollow" }
    ],
    links: [{ rel: "canonical", href: "https://app.salesko.ai/login" }]
  }),
  component: LoginRoute
});

function LoginRoute() {
  const { availableAuthProviders, handleAuthProviderSignIn, storedSession } = useAgentSession();
  const { locale, t } = useI18n();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const displayName = storedSession?.name ?? storedSession?.email ?? t.common.account;
  const displaySub = storedSession
    ? [storedSession.email, `tenant ${shortScopeId(storedSession.tenantId)}`].filter(Boolean).join(" · ")
    : null;

  useEffect(() => {
    if (!storedSession) {
      return;
    }
    const pendingCheckout = readPendingBillingCheckoutIntent();
    if (!pendingCheckout) {
      return;
    }
    const checkoutProductId = pendingCheckout.productId;
    const checkoutReturnPath = pendingCheckout.returnPath;

    let active = true;
    async function resumeCheckout() {
      try {
        const checkout = await startCheckout(checkoutProductId, checkoutReturnPath);
        if (active) {
          consumePendingBillingCheckoutIntent();
          window.location.replace(checkout.checkoutUrl);
        }
      } catch (error) {
        if (active) {
          setCheckoutError(error instanceof Error ? error.message : "billing_checkout_failed");
        }
      }
    }

    void resumeCheckout();
    return () => {
      active = false;
    };
  }, [storedSession]);

  return (
    <main className="login-shell" aria-label="Salesko sign in" lang={LOCALE_HTML_LANG[locale]}>
      <section className="login-visual" aria-label="Salesko">
        <a className="brand-lockup login-brand" href="/" aria-label="Salesko home">
          <span className="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g stroke="#bcae97" strokeWidth="1.5" strokeLinecap="round">
                <line x1="19" y1="21" x2="31.5" y2="10" />
                <line x1="19" y1="21" x2="7.5" y2="14" />
                <line x1="19" y1="21" x2="23" y2="33.5" />
              </g>
              <circle cx="31.5" cy="10" r="3.4" fill="#6ba872" />
              <circle cx="7.5" cy="14" r="3.1" fill="#d9974f" />
              <circle cx="23" cy="33.5" r="3" fill="#cba94f" />
              <circle cx="19" cy="21" r="6" fill="#4a8cb0" />
              <circle cx="19" cy="21" r="2.1" fill="#ffffff" />
            </svg>
          </span>
          <span className="brand-copy">
            <strong>Salesko</strong>
            <span>Contacts Intelligence</span>
          </span>
        </a>

        <div className="login-figure" aria-hidden="true">
          <span className="login-figure-grid" />
          <svg className="login-edges" viewBox="0 0 100 100" preserveAspectRatio="none">
            <line className="is-lit" x1="46" y1="44" x2="78" y2="22" />
            <line className="is-lit" x1="46" y1="44" x2="80" y2="76" />
            <line x1="46" y1="44" x2="18" y2="26" />
            <line x1="46" y1="44" x2="20" y2="74" />
            <line x1="18" y1="26" x2="20" y2="74" />
          </svg>

          <span className="login-node login-node-hub is-person" style={{ left: "46%", top: "44%" }}>
            <span className="login-avatar">AC</span>
            <strong>Alice Chen</strong>
            <span>VP Product · OpenAI</span>
          </span>
          <span className="login-node is-company" style={{ left: "78%", top: "22%", "--n-delay": "60ms" } as React.CSSProperties}>
            <span className="dot" />
            OpenAI
          </span>
          <span className="login-node is-person" style={{ left: "18%", top: "26%", "--n-delay": "120ms" } as React.CSSProperties}>
            <span className="dot" />
            David Li
          </span>
          <span
            className="login-node is-event"
            style={{ left: "20%", top: "74%", "--n-delay": "180ms" } as React.CSSProperties}
          >
            <span className="dot" />
            Singapore dinner
          </span>
          <span
            className="login-node is-opportunity"
            style={{ left: "80%", top: "76%", "--n-delay": "240ms" } as React.CSSProperties}
          >
            <span className="dot" />
            Acme Taiwan
          </span>
        </div>

        <p className="login-tagline">{t.login.tagline}</p>
      </section>

      <section className="login-panel" aria-label="Sign in">
        <div className="login-card">
          <LanguageSwitcher className="login-lang" />

          <div className="login-copy">
            <h1>{storedSession ? t.login.headingIn : t.login.headingOut}</h1>
            <p>{storedSession ? t.login.subIn : availableAuthProviders.length > 0 ? t.login.subOut : t.login.subOutUnavailable}</p>
          </div>

          {storedSession ? (
            <div className="login-session">
              <div className="login-session-id">
                <span className="account-avatar" aria-hidden="true">
                  {initialsOf(displayName)}
                </span>
                <div>
                  <strong>{displayName}</strong>
                  {displaySub ? <span>{displaySub}</span> : null}
                </div>
              </div>
              <a className="login-cta" href="/app">
                <span>{t.common.openWorkspace}</span>
                <ArrowRight size={17} />
              </a>
              {checkoutError ? (
                <p className="login-muted" role="alert">
                  {checkoutError}
                </p>
              ) : null}
              <button className="login-quiet" type="button" onClick={() => clearStoredSaleskoSession()}>
                <LogOut size={15} />
                <span>{t.common.signOut}</span>
              </button>
            </div>
          ) : (
            <div className="login-providers">
              {availableAuthProviders.length > 0 ? (
                availableAuthProviders.map((provider) => {
                  const meta = PROVIDER_META[provider];
                  const Icon = meta.icon;
                  return (
                    <button
                      key={provider}
                      className="login-provider"
                      type="button"
                      onClick={() => handleAuthProviderSignIn(provider)}
                    >
                      {provider === "google" ? <GoogleGlyph size={18} /> : Icon ? <Icon size={18} /> : null}
                      <span>{t.login.continueWith(meta.label)}</span>
                    </button>
                  );
                })
              ) : (
                <p className="login-muted" role="status">
                  {t.login.notConfigured}
                </p>
              )}
              <a className="login-quiet" href="/app">
                <span>{t.login.openWithout}</span>
                <ArrowRight size={15} />
              </a>
            </div>
          )}

          <p className="login-fineprint">
            {t.login.fineLead}
            <a href={TERMS_URL}>{t.login.terms}</a>
            {t.login.and}
            <a href={PRIVACY_URL}>{t.login.privacy}</a>
            {t.login.period}
          </p>
        </div>
      </section>
    </main>
  );
}

function initialsOf(value: string) {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "S";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function shortScopeId(value: string) {
  if (value.length <= 14) {
    return value;
  }
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}
